import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  wwwWebFilterList,
  useListWwwWebProjects,
} from '@/api/generated/www-web-filters/www-web-filters';
import type { WebFilterListItem } from '@/api/generated/schemas';
import { Alert, Badge, Button, Select } from '@/shared/ui';
import { col, type ListPageConfig } from '@/shared/ui/list-page';
import { NavListPage } from '@/shared/navigation/react/NavListPage';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { sendMagicOpenForm } from '@/shared/legacyMagicBridge/sendDesktopAction';
import { WEB_FILTER_LIST_KEY } from '../internal/webFiltersQueryKeys';
import { webFilterFormAgenda } from '../createDetail/webFilterFormAgenda';
import { webFilterListAgenda } from './webFilterListAgenda';

function buildWebFilterListConfig(
  openFilter: (id: string | undefined) => void,
  webProjectId: number
): ListPageConfig<WebFilterListItem> {
  return {
    key: WEB_FILTER_LIST_KEY,
    i18n: { namespace: 'www' },
    api: { list: (request, signal) => wwwWebFilterList(request, signal) },
    // List vždy patří jednomu webovému projektu — rámec je skrytý filtr, ne uživatelský.
    scope: [{ field: 'webProjectId', ids: [String(webProjectId)] }],
    rowKey: (item) => item.id,
    defaultSort: { column: 'name', desc: false },
    fulltext: { placeholder: (t) => t('webFilterList.search') },
    onOpen: (item) => openFilter(item.id),
    create: {
      label: (t) => t('webFilterList.new'),
      onClick: () => openFilter(undefined),
    },
    columns: (t) => [
      col.strong<WebFilterListItem>('name', {
        label: t('webFilterList.cols.name'),
        w: 260,
        frozen: true,
      }),
      col.text<WebFilterListItem>('criteriaSummary', {
        label: t('webFilterList.cols.filter'),
        w: 320,
      }),
      col.text<WebFilterListItem>('displaySummary', {
        label: t('webFilterList.cols.display'),
        w: 320,
      }),
      col.custom<WebFilterListItem>({
        key: 'status',
        label: t('webFilterList.cols.status'),
        w: 110,
        sortable: false,
        cell: () => <Badge tone="neutral">{t('webFilterList.statusPlaceholder')}</Badge>,
      }),
    ],
  };
}

interface WebFilterListPageProps {
  layerId: string;
}

export function WebFilterListPage({ layerId }: WebFilterListPageProps) {
  const { t } = useTranslation('www');
  const navigator = useNavigator();
  const { state } = useAgendaParams(webFilterListAgenda, layerId);
  const { patch, remove } = useAgendaState(webFilterListAgenda, layerId);

  const projectsQuery = useListWwwWebProjects();
  const projects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);

  useEffect(() => {
    if (projectsQuery.isLoading || projectsQuery.isError) return;
    if (projects.length === 0) {
      if (state.webProjectId != null) remove('webProjectId');
      return;
    }
    if (state.webProjectId != null && projects.some((p) => p.id === state.webProjectId)) {
      return;
    }
    if (projects.length === 1) {
      patch({ webProjectId: projects[0].id });
      return;
    }
    if (state.webProjectId != null && !projects.some((p) => p.id === state.webProjectId)) {
      remove('webProjectId');
    }
  }, [projects, projectsQuery.isLoading, projectsQuery.isError, state.webProjectId, patch, remove]);

  const openFilter = useCallback(
    (id: string | undefined) => {
      void navigator.openModal(
        webFilterFormAgenda,
        { webFilterId: id },
        id ? undefined : { state: { webProjectId: state.webProjectId } }
      );
    },
    [navigator, state.webProjectId]
  );

  const config = useMemo(() => {
    if (state.webProjectId == null) return null;
    return buildWebFilterListConfig(openFilter, state.webProjectId);
  }, [openFilter, state.webProjectId]);

  if (projectsQuery.isLoading) {
    return (
      <p className="text-phx-text-muted text-phx-sm p-phx-5">
        {t('webFilterList.loadingProjects')}
      </p>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="gap-phx-3 p-phx-5 flex max-w-xl flex-col" data-testid="web-filter-list-empty">
        <Alert severity="info" title={t('webFilterList.noProjectsTitle')}>
          {t('webFilterList.noProjectsDescription')}
        </Alert>
        <div>
          <Button
            type="button"
            variant="primary"
            onClick={() => sendMagicOpenForm('WebProject.List')}
          >
            {t('webFilterList.openWebProjects')}
          </Button>
        </div>
      </div>
    );
  }

  const projectOptions = projects.map((p) => ({
    value: String(p.id),
    label: p.isActive ? p.name : `${p.name} (${t('webFilter.inactiveProject')})`,
  }));

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="web-filter-list">
      <div className="border-phx-border-1 gap-phx-3 px-phx-5 py-phx-3 flex shrink-0 items-center border-b">
        <span className="text-phx-sm text-phx-text font-semibold">
          {t('webFilterList.projectScope')}
        </span>
        <Select
          className="max-w-sm"
          value={state.webProjectId != null ? String(state.webProjectId) : ''}
          options={projectOptions}
          placeholder={t('webFilterList.pickProject')}
          onChange={(event) => {
            const id = Number(event.target.value);
            if (Number.isFinite(id) && id > 0) patch({ webProjectId: id });
            else remove('webProjectId');
          }}
          data-testid="web-filter-project-scope"
        />
      </div>
      {config && state.webProjectId != null ? (
        <div className="min-h-0 flex-1">
          <NavListPage agenda={webFilterListAgenda} layerId={layerId} config={config} />
        </div>
      ) : (
        <p className="text-phx-text-muted text-phx-sm p-phx-5">
          {t('webFilterList.pickProjectHint')}
        </p>
      )}
    </div>
  );
}
